import { renderToString } from 'react-dom/server'
import { objectFromEntries, sum } from '../../utils'
import { exemptionCategories, requirements as requirementsConfig } from '../../exemptions'

export const getProjectExemptions = (project, data) => {
    return (
        project.children
            .map(child => (data[child.id] ? data[child.id].exemptions : []))
            .reduce((acc, exemptions) => {
                if (acc === null) {
                    return exemptions
                }

                return objectFromEntries(Object.entries(exemptions).map(([k, v]) => [k, v && acc[k]]))
            }, null) || []
    )
}

export const getProjectRequirements = (project, data) => {
    return (
        project.children
            .map(child => (data[child.id] ? data[child.id].requirements : {}))
            .reduce((acc, requirements) => {
                if (acc === null) {
                    return requirements
                }

                return objectFromEntries(Object.entries(requirements).map(([k, v]) => [k, v && acc[k]]))
            }, null) || {}
    )
}

export const createReportData = (items, data, include, renderComponents = true) => {
    const resolveRequirement = (requirement, requirements) => {
        let config
        if (typeof requirement === 'string') {
            config = { id: requirement, ...requirementsConfig[requirement] }
        } else if (requirement.id) {
            config = { ...requirement, ...requirementsConfig[requirement.id] }
        } else {
            config = requirement
        }

        let isMet = false
        if (config.id || config.requirements) {
            if (config.requirements) {
                const reqs = config.requirements.map(req => resolveRequirement(req, requirements))
                const trueCt = reqs.filter(r => r.isMet === true).length
                const falseCt = reqs.filter(r => r.isMet === false).length
                const maybeCt = reqs.filter(r => r.isMet === 'maybe').length

                if (config.op === 'and') {
                    if (falseCt) {
                        isMet = false
                    } else {
                        isMet = maybeCt ? 'maybe' : true
                    }
                } else if (!trueCt && !maybeCt) {
                    isMet = false
                } else {
                    isMet = trueCt ? true : 'maybe'
                }
            } else {
                isMet = requirements[config.id] === null ? 'maybe' : requirements[config.id]
            }
        }

        if (config.requirements && config.show) {
            config = {
                ...config,
                requirements: config.requirements.map(req => resolveRequirement(req, requirements))
            }
        }

        if (!isMet && config.strict === false) {
            isMet = 'maybe'
        }

        return {
            ...config,
            isMet,
            description: renderComponents ? renderToString(config.description) : config.description
        }
    }

    const resolveExemption = (item, exemption, { requirements: itemRequirements, exemptions }) => {
        const { code, notes, requirements } = exemption
        const { spatial, project } = typeof requirements === 'function' ? requirements(itemRequirements) : requirements

        const spatialReqs = spatial.map(req => resolveRequirement(req, itemRequirements))

        let isMet
        if (spatialReqs.length < 1) {
            isMet = 'maybe'
        } else if (!(code in exemptions)) {
            isMet = false
        } else if (exemptions[code] === null) {
            isMet = 'maybe'
        } else {
            isMet = exemptions[code]
            if (!isMet && spatialReqs.every(req => req.isMet === true || req.isMet === 'maybe')) {
                isMet = 'maybe'
            }
        }

        return {
            ...exemption,
            notes: renderComponents ? renderToString(notes) : notes,
            requirements: {
                spatial: spatialReqs,
                project: project.map(req => resolveRequirement(req, itemRequirements))
            },
            isMet
        }
    }

    const resolveCategory = (item, category, itemData) => ({
        ...category,
        description: renderComponents ? renderToString(category.description) : category.description,
        exemptions: category.exemptions.map(exemption => resolveExemption(item, exemption, itemData))
    })

    let includeExemption = (exemption, itemExemptions) => true // eslint-disable-line no-unused-vars
    if (include === 'potential') {
        includeExemption = ({ code, requirements: { spatial } }, itemExemptions) =>
            spatial.length && !!itemExemptions[code]
    } else if (include === 'possible') {
        includeExemption = ({ code, requirements: { spatial } }, itemExemptions) =>
            (code in itemExemptions && itemExemptions[code] !== false) ||
            spatial.every(req => req.isMet || req.strict === false)
    }

    return {
        items: items.map(item => {
            let requirements
            let exemptions
            if (item.type === 'project') {
                requirements = getProjectRequirements(item, data)
                exemptions = getProjectExemptions(item, data)
            } else {
                requirements = data[item.id].requirements
                exemptions = data[item.id].exemptions
            }

            const categories = exemptionCategories
                .map(category =>
                    resolveCategory(item.id, category, {
                        requirements,
                        exemptions
                    })
                )
                .map(category => ({
                    ...category,
                    exemptions: category.exemptions.filter(exemption => includeExemption(exemption, exemptions))
                }))
                .filter(({ exemptions }) => exemptions.length > 0)

            return {
                ...item,
                exemptions: categories,
                potential_count: sum(
                    categories.map(category => category.exemptions.filter(ex => ex.isMet === true).length)
                ),
                possible_count: sum(
                    categories.map(category => category.exemptions.filter(ex => ex.isMet === 'maybe').length)
                )
            }
        })
    }
}
